import { useCallback, useEffect, useRef, useState } from 'react';
import type { QuickRepliesResponse, QuickReply } from '@chat-emp/shared';

import type { QuickReplySource } from '../conversation/QuickReplies.js';
import { QUICK_REPLIES } from '../fixtures/data.js';
import type { Message } from '../fixtures/domain.js';
import { apiFetch } from '../lib/api.js';
import { selectQuickReply } from '../lib/facade.js';

interface UseQuickReplies {
  replies: string[];
  loading: boolean;
  source: QuickReplySource;
  /**
   * Report that the employer picked this suggestion, so the upstream audit row
   * records which of the shown options was used. A no-op for the static openers,
   * which have no audit to attribute to.
   */
  select: (text: string) => void;
}

/** What the hook holds internally: ids and the audit id never reach the UI, which
 *  only ever renders text. Keeping them here avoids threading them through
 *  `QuickReplies` and `ChatView` just to hand them back to the BFF. */
interface Batch {
  replies: QuickReply[];
  auditId?: string;
  loading: boolean;
  source: QuickReplySource;
}

const STATIC: Batch = {
  replies: QUICK_REPLIES.map((text) => ({ id: '', text })),
  loading: false,
  source: 'static',
};

/**
 * Quick-reply suggestions for the open conversation. When `enabled` (a live chat
 * with messages) it asks the BFF for AI-generated openers from the transcript;
 * otherwise — new/empty chats, no session, or any failure — it serves the static
 * openers. Never load-bearing: a failure degrades to `static`, never an error.
 *
 * Refreshes per conversation and as it grows (keyed on channel + message
 * count + last message id), not on every render.
 */
export function useQuickReplies(
  channelId: string | null,
  messages: Message[],
  isOwnMessage: (message: Message) => boolean,
  enabled: boolean,
): UseQuickReplies {
  const [state, setState] = useState<Batch>(STATIC);

  // Read the latest messages/ownership inside the effect via refs, so the effect
  // only re-runs on the signature below — not on every new array identity.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const isOwnRef = useRef(isOwnMessage);
  isOwnRef.current = isOwnMessage;

  const signature = `${channelId ?? ''}:${messages.length}:${messages[messages.length - 1]?.id ?? ''}`;

  useEffect(() => {
    const msgs = messagesRef.current;
    if (!enabled || !channelId || msgs.length === 0) {
      setState(STATIC);
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    const transcript = msgs.map((m) => ({
      role: (isOwnRef.current(m) ? 'employer' : 'applicant') as 'employer' | 'applicant',
      text: m.text,
    }));

    apiFetch<QuickRepliesResponse>('/api/chat/quick-replies', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ messages: transcript }),
    })
      .then(({ replies, auditId }) => {
        if (cancelled) return;
        setState(
          replies.length > 0
            ? { replies, loading: false, source: 'ai', ...(auditId ? { auditId } : {}) }
            : STATIC,
        );
      })
      .catch(() => {
        if (!cancelled) setState(STATIC);
      });

    return () => {
      cancelled = true;
    };
  }, [signature, enabled, channelId]);

  // Held in a ref so `select` stays stable and always reads the batch actually on
  // screen, rather than closing over whichever render created it.
  const batchRef = useRef(state);
  batchRef.current = state;

  const select = useCallback((text: string) => {
    const { replies, auditId } = batchRef.current;
    const picked = replies.find((r) => r.text === text);
    // Static openers carry no id, and a batch generated without an audit row
    // cannot be attributed — nothing to report in either case.
    if (!auditId || !picked?.id) return;
    void selectQuickReply({ auditId, selectedId: picked.id, selectedText: picked.text });
  }, []);

  return {
    replies: state.replies.map((r) => r.text),
    loading: state.loading,
    source: state.source,
    select,
  };
}
